<script>
import { mapState } from 'vuex'
import date from 'locutus/php/datetime/date'
import FormViewer from '@components/admin/form-viewer'
import {
  fetchTicket,
  updateTicket,
  closeTicket,
  replyTicket,
} from '@apis/admin/cs'
import fromPairs from 'lodash/fromPairs'

export default {
  components: { FormViewer },
  props: {
    ticket_seq: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      loading: false,
      info: {},
      field_values: [],
      forms: [],
      reply: null,
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
  },
  mounted() {
    this.init()
  },
  methods: {
    init() {
      Promise.all([this.loadInfo()])
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadInfo() {
      this.loading = true
      const { ticket_info, field_values, forms } = await fetchTicket(
        this.ticket_seq
      )

      this.info = ticket_info
      this.reply = ticket_info.reply
      this.field_values = field_values
      this.forms = forms
    },
    handleTicketReply() {
      this.loading = true
      this.$confirm(
        this.$t('cs_ticket_info.msg_inquiry_answer'),
        this.$t('admin.modal_confirm'),
        {
          type: 'warning',
        }
      )
        .then(() =>
          updateTicket({
            seq: this.info.seq,
            reply: this.reply,
          })
        )
        .then(({ seq }) => {
          this.$emit('submit', seq)
        })
        .then(() =>
          this.$toasted.success(
            this.$t('cs_ticket_info.msg_inquiry_answer_success')
          )
        )
        .then(() => this.init())
        .then(() => {
          this.reply = this.info.reply
        })
        .catch((e) => (e ? this.$toasted.error(e.message) : null))
        .finally(() => {
          this.loading = false
        })
    },
    async handleCloseTicket() {
      try {
        this.loading = true
        if (
          !(await this.$confirm(
            this.$t('cs_ticket_info.msg_inquiry_ending'),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        await closeTicket(this.info.seq)
        await this.init()
        await this.$emit('submit', this.info.seq)
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    async handleReplyCompleteTicket() {
      try {
        this.loading = true
        if (
          !(await this.$confirm(
            this.$t('cs_ticket_info.msg_inquiry_answer_ending'),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        await replyTicket(this.info.seq)
        await this.init()
        await this.$emit('submit', this.info.seq)
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    date,
    fromPairs,
  },
}
</script>

<template>
  <div v-loading="loading">
    <h3 :class="$style.xeAdmin">{{ $t('admin.default_info') }}</h3>
    <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
      <colgroup>
        <col width="150" />
        <col width="*" />
      </colgroup>
      <tbody>
        <tr>
          <th scope="row"
            ><div>{{ $t('admin.kyc_cdd_name_id') }}</div></th
          >
          <td
            >{{ info.user_name || $t('cs_ticket_info.non_member') }} ({{
              info.user_id || $t('cs_ticket_info.non_member')
            }})</td
          >
        </tr>
        <tr>
          <th scope="row"
            ><div>{{ $t('admin.email_address') }}</div></th
          >
          <td>{{ info.email_address }}</td>
        </tr>
        <tr>
          <th scope="row"
            ><div>{{ $t('admin.close_date') }}</div></th
          >
          <td>{{
            info.closed_date === null
              ? $t('admin.no_close')
              : date('Y-m-d H:i:s', new Date(info.closed_date))
          }}</td>
        </tr>
        <tr>
          <th scope="row"
            ><div>{{ $t('admin.regdate') }}</div></th
          >
          <td>{{ date('Y-m-d H:i:s', new Date(info.regdate)) }}</td>
        </tr>
        <tr>
          <th scope="row"
            ><div>{{ $t('admin.status') }}</div></th
          >
          <td>
            {{ fromPairs(constants.cs.ticket.status)[info.status] }}
          </td>
        </tr>
        <tr>
          <th scope="row"
            ><div>{{ $t('cs_ticket_info.title') }}</div></th
          >
          <td>
            {{ info.title }}
          </td>
        </tr>
        <tr>
          <th scope="row" colspan="2" style="text-align:center;"
            ><div>{{ $t('cs_ticket_info.content') }}</div></th
          >
        </tr>
        <tr>
          <td colspan="2">
            {{ info.content || $t('cs_ticket_info.nodone') }}
          </td>
        </tr>
        <tr>
          <th colspan="2" style="text-align:center;">
            <div>{{ $t('cs_ticket_info.answer') }}</div>
          </th>
        </tr>
        <tr>
          <td colspan="2">
            <quill-editor
              ref="editor"
              v-model="reply"
              style="min-height:200px;"
            />
          </td>
        </tr>
        <tr>
          <th colspan="2" :class="$style.button">
            <a
              :class="[$style.button, $style.gray, $style.strong]"
              @click.prevent="handleTicketReply"
              ><span>{{ $t('cs_ticket_info.answer_register') }}</span></a
            >
            &nbsp;
            <template v-if="info.status === 'PENDING'">
              <a
                :class="[$style.button, $style.blue]"
                @click.prevent="handleReplyCompleteTicket"
                ><span>{{ $t('cs_ticket_info.answer_success') }}</span></a
              >
              &nbsp;
              <a
                :class="[$style.button, $style.red]"
                @click.prevent="handleCloseTicket"
                ><span>{{ $t('cs_ticket_info.ending') }}</span></a
              >
            </template>
          </th>
        </tr>
      </tbody>
    </table>
    <FormViewer v-bind="{ field_values, forms }"></FormViewer>
  </div>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';
</style>
